import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import {
  DataGridPro,
} from '@mui/x-data-grid-pro';
import {
  Refresh as RefreshIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  BugReport as BugReportIcon,
} from '@mui/icons-material';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonateDashboardPageSx,
  harmonateDashboardTitleSx,
  harmonateKpiCardSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateSectionHeaderSx,
  harmonatePlainIconButtonSx,
} from '../../theme/harmonateStyles';

const Dashboard = () => {
  // State Management
  const [workflowLoading, setWorkflowLoading] = useState(true);
  const [error, setError] = useState('');
  const [workflowData, setWorkflowData] = useState([]);
  
  // New state for API card data
  const [cardData, setCardData] = useState({
    documentsForReview: { count: 0, loading: true },
    documentsUploaded: { count: 0, loading: true },
    documentsApproved: { count: 0, loading: true },
    failedDocuments: { count: 0, loading: true }
  });

  // API Base URL
  const API_BASE = `${process.env.REACT_APP_BACKEND_URL}conductor`;
  const token = localStorage.getItem('token');

  const getTenantId = () => {
    return localStorage.getItem('tenant_id');
  };

  // --- Fetch Workflow Data ---
  const fetchWorkflowData = async () => {
    try {
      setWorkflowLoading(true);
      const tenantId = getTenantId();
      if (!tenantId) throw new Error('No tenant ID found');

      // Fetch all workflows
      const url = `${process.env.REACT_APP_BACKEND_URL}conductor/docs/workflow-stats/${tenantId}?`;
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!resp.ok) {
        throw new Error(`Failed to fetch workflow data: ${resp.status}`);
      }
      
      const data = await resp.json();
      
      const transformedData = [];
      if (Array.isArray(data.workflowStats)) {
        data.workflowStats.forEach(item => {
          transformedData.push({
            id: item.workflowId,
            documentType: item.workflowName,
            inProcess: item.inProcessCount || 0,
            awaitingApproval: item.awaitingApprovalCount || 0,
            complete: item.completedCount || 0,
            failed: item.failedCount || 0,
            total: item.count || 0
          });
        });
      }
      
      if (data.unidentifiedCount > 0) {
        transformedData.push({
          id: 'unidentified',
          documentType: 'Unidentified Documents',
          inProcess: data.unidentifiedInProcessCount || 0,
          awaitingApproval: data.unidentifiedAwaitingApprovalCount || 0,
          complete: data.unidentifiedCompletedCount || 0,
          failed: data.unidentifiedFailedCount || 0,
          total: data.unidentifiedCount || 0
        });
      }
      
      setWorkflowData(transformedData);
    } catch (err) {
      console.error('Error fetching workflow data:', err);
      setError(err.message);
    } finally {
      setWorkflowLoading(false);
    }
  };

  // --- Fetch API Card Data ---
  const fetchCardData = async () => {
    const tenantId = getTenantId();
    
    try {
      setCardData(prev => ({
        documentsForReview: { ...prev.documentsForReview, loading: true },
        documentsUploaded: { ...prev.documentsUploaded, loading: true },
        documentsApproved: { ...prev.documentsApproved, loading: true },
        failedDocuments: { ...prev.failedDocuments, loading: true }
      }));

      const [reviewResponse, uploadedResponse, approvedResponse, failedResponse] = await Promise.allSettled([
        fetch(`${API_BASE}/dashboard/total-documents-for-review?tenantId=${tenantId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE}/dashboard/total-documents?tenantId=${tenantId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE}/dashboard/total-documents-approved?tenantId=${tenantId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE}/dashboard/total-failed-documents?tenantId=${tenantId}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
      ]);

      if (reviewResponse.status === 'fulfilled' && reviewResponse.value.ok) {
        const reviewData = await reviewResponse.value.json();
        setCardData(prev => ({
          ...prev,
          documentsForReview: { count: reviewData.count || 0, loading: false }
        }));
      } else {
        setCardData(prev => ({
          ...prev,
          documentsForReview: { count: 0, loading: false }
        }));
      }

      if (uploadedResponse.status === 'fulfilled' && uploadedResponse.value.ok) {
        const uploadedData = await uploadedResponse.value.json();
        setCardData(prev => ({
          ...prev,
          documentsUploaded: { count: uploadedData.count || 0, loading: false }
        }));
      } else {
        setCardData(prev => ({
          ...prev,
          documentsUploaded: { count: 0, loading: false }
        }));
      }

      if (approvedResponse.status === 'fulfilled' && approvedResponse.value.ok) {
        const approvedData = await approvedResponse.value.json();
        setCardData(prev => ({
          ...prev,
          documentsApproved: { count: approvedData.count || 0, loading: false }
        }));
      } else {
        setCardData(prev => ({
          ...prev,
          documentsApproved: { count: 0, loading: false }
        }));
      }

      if (failedResponse.status === 'fulfilled' && failedResponse.value.ok) {
        const failedData = await failedResponse.value.json();
        setCardData(prev => ({
          ...prev,
          failedDocuments: { count: failedData.count || 0, loading: false }
        }));
      } else {
        setCardData(prev => ({
          ...prev,
          failedDocuments: { count: 0, loading: false }
        }));
      }

    } catch (err) {
      console.error('Error fetching card data:', err);
      setError('Failed to fetch dashboard card data');
      setCardData({
        documentsForReview: { count: 0, loading: false },
        documentsUploaded: { count: 0, loading: false },
        documentsApproved: { count: 0, loading: false },
        failedDocuments: { count: 0, loading: false }
      });
    }
  };

  const refreshData = () => {
    fetchCardData();
    fetchWorkflowData();
  };

  useEffect(() => {
    fetchCardData();
    fetchWorkflowData();
  }, []);

  const summaryData = [
    {
      label: 'Documents to Review',
      value: cardData.documentsForReview.loading ? 
        <CircularProgress size={20} sx={{ color: ht.green }} /> : 
        cardData.documentsForReview.count,
      icon: <AssignmentIcon fontSize="large" sx={{ color: ht.text }} />,
      change: '+1.8%',
      changeType: 'up',
      sub: 'Pending review',
      loading: cardData.documentsForReview.loading,
      disabled: false
    },
    {
      label: 'Total Documents Uploaded',
      value: cardData.documentsUploaded.loading ? 
        <CircularProgress size={20} sx={{ color: ht.green }} /> : 
        cardData.documentsUploaded.count,
      icon: <CloudUploadIcon fontSize="large" sx={{ color: ht.text }} />,
      change: '+12.5%',
      changeType: 'up',
      sub: 'Total uploaded',
      loading: cardData.documentsUploaded.loading,
      disabled: false
    },
    {
      label: 'Total Documents Approved',
      value: cardData.documentsApproved.loading ? 
        <CircularProgress size={20} sx={{ color: ht.green }} /> : 
        cardData.documentsApproved.count,
      icon: <CheckCircleIcon fontSize="large" sx={{ color: ht.green }} />,
      change: '+8.3%',
      changeType: 'up',
      sub: 'Approved documents',
      loading: cardData.documentsApproved.loading,
      disabled: false
    },
    {
      label: 'Failed Documents',
      value: cardData.failedDocuments.loading ? 
        <CircularProgress size={20} sx={{ color: ht.green }} /> : 
        cardData.failedDocuments.count,
      icon: <BugReportIcon fontSize="large" sx={{ color: ht.red }} />,
      change: '+5.2%',
      changeType: 'up',
      sub: 'Failed processing',
      loading: cardData.failedDocuments.loading,
      disabled: false
    }
  ];

  return (
    <Box sx={harmonateDashboardPageSx}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
        <Typography sx={harmonateDashboardTitleSx}>
          Document Dashboard
        </Typography>
        <IconButton
          onClick={refreshData}
          disabled={cardData.documentsForReview.loading || cardData.documentsUploaded.loading || cardData.documentsApproved.loading || cardData.failedDocuments.loading || workflowLoading}
          size="small"
          aria-label="Refresh dashboard"
          sx={harmonatePlainIconButtonSx}
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2, flexShrink: 0 }}>
        {summaryData.map((item, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Card 
              sx={{ 
                ...harmonateKpiCardSx,
                opacity: item.disabled ? 0.4 : 1,
                pointerEvents: item.disabled ? 'none' : 'auto',
                bgcolor: item.disabled ? ht.listBg : ht.white,
                position: 'relative'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ opacity: item.disabled ? 0.5 : 1 }}>
                  {item.icon}
                </Box>
                <Box sx={{ ml: 2 }}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ color: item.disabled ? ht.fadedText : ht.text }}
                  >
                    {item.label}
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      color: item.disabled ? ht.fadedText : ht.darkBlue
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                color: item.disabled ? ht.fadedText : 
                       item.changeType === 'up' ? ht.green : 
                       item.changeType === 'down' ? ht.red : 
                       ht.text
              }}>
                {item.changeType === 'up' && <ArrowUpwardIcon fontSize="small" />}
                {item.changeType === 'down' && <ArrowDownwardIcon fontSize="small" />}
                <Typography 
                  variant="body2" 
                  sx={{ ml: 0.5, fontWeight: 500 }}
                >
                  {item.change}
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ ml: 1, color: item.disabled ? ht.fadedText : ht.fadedText }}
                >
                  {item.sub}
                </Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper elevation={0} sx={{ 
        ...harmonateCardSx,
        height: 500,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        flex: 1,
        minHeight: 0,
      }}>
        <Box sx={{ ...harmonateSectionHeaderSx, flexShrink: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.text }}>
            Workflows
          </Typography>
        </Box>
        {workflowLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress sx={{ color: ht.green }} />
          </Box>
        ) : (
          <DataGridPro
            rows={workflowData}
            rowHeight={40}
            columns={[
              { 
                field: 'documentType', 
                headerName: 'Workflow Name', 
                flex: 1,
                resizable: true,
                renderCell: (params) => (
                  <Box sx={{ fontWeight: params.row.id === 'unidentified' ? 'bold' : 'normal' }}>
                    {params.value}
                  </Box>
                )
              },
              { 
                field: 'inProcess', 
                headerName: 'In Process', 
                width: 120,
                resizable: true,
                align: 'center',
                valueFormatter: (params) => params.value !== undefined ? params.value : '0'
              },
              { 
                field: 'awaitingApproval', 
                headerName: 'Awaiting Approval', 
                width: 150,
                resizable: true,
                align: 'center',
                valueFormatter: (params) => params.value !== undefined ? params.value : '0'
              },
              { 
                field: 'complete', 
                headerName: 'Completed', 
                width: 120,
                resizable: true,
                align: 'center',
                valueFormatter: (params) => params.value !== undefined ? params.value : '0'
              },
              { 
                field: 'failed', 
                headerName: 'Failed', 
                width: 100,
                resizable: true,
                align: 'center',
                valueFormatter: (params) => params.value !== undefined ? params.value : '0',
                cellClassName: (params) => params.value > 0 ? 'error-cell' : ''
              },
            ]}
            sx={{ 
              ...harmonateDataGridSx,
              flex: 1,
            }}
            disableSelectionOnClick
            pagination
            rowsPerPageOptions={[10, 25, 50]}
            pageSize={10}
            autoPageSize={false}
          />
        )}
      </Paper>
    </Box>
  );
};

export default Dashboard;
